/**
 * Store SIP per gestire lo stato e le azioni relative alle chiamate VoIP.
 */

import { defineStore } from "pinia";
import { markRaw } from "vue";
import { setupSIP } from "../sip/sip-setup";
import type { Invitation } from "sip.js";
import type { SipCredentials } from "@/types/sipCredentials";
import { getInvitationInfoFromPhoneNumber } from "@/api/apiclient/lead";
import { answerCall, type AnswerOutcome } from "@/sip/sip-call-manager";
import { saveSnapshot, clearSnapshot } from "@/sip/callRecoverySnapshot";
import {
  postPrefillNumber,
  type SipCallStateSnapshot,
} from "@/components/SIPIntegration/composables/useSipBroadcast";
import { openSipPopupIfClosed } from "@/composables/useSipPopupManager";
import { setPendingPrefill } from "@/sip/sipPendingPrefill";
import { extractCallerLine } from "@/sip/callerLine";

// Riferimento DOM tenuto fuori dallo state Pinia: non è serializzabile e non
// deve essere reattivo. Un solo elemento per tutta la vita dell'app.
let remoteAudioElement: HTMLAudioElement | null = null;

export type SipConnectionFailureType = "transport" | "auth" | "timeout";

export interface SipConnectionFailure {
  type: SipConnectionFailureType;
  message: string;
  // Ritardo suggerito dal server prima del prossimo tentativo (header SIP
  // Retry-After, es. su un 503). In millisecondi. Quando presente la state
  // machine di useSipConnectionRetry lo usa come delay dell'auto-retry al posto
  // del default. Vedi GAIA-2589.
  retryAfterMs?: number;
}

export const useSipStore = defineStore("sip", {
  state: () => ({
    phoneNumber: "",
    // True quando `phoneNumber` è il CallerID scritto da una chiamata in arrivo e
    // non un numero che l'operatore ha scelto di comporre. Serve a distinguere
    // "il numero è finito qui da solo" da "l'operatore vuole chiamarlo": il tasto
    // verde della barra cambia significato quando lo squillo finisce, e senza
    // questa distinzione un click di troppo partiva verso il cliente (GAIA-2664).
    phoneNumberFromInbound: false,
    // Linea da cui è entrata la chiamata, quando il centralino la dichiara nel
    // display-name (CID Name Prefix delle Inbound Route). Con più numeri che
    // convergono sulla stessa coda è l'unica cosa che dice all'operatore quale
    // servizio il cliente stia cercando. Null quando il display-name non
    // aggiunge niente al numero. Vedi `extractCallerLine`.
    callerLine: null as string | null,
    targetUrl: "",
    isRegistered: false,
    isLoading: false,
    error: null as string | null,

    // Configurazione Utente
    extension: "" as string,

    // chiamata corrente (solo per inbound viene settato da onInvite)
    incomingCall: null as Invitation | null,

    // stato generico chiamata (in/out)
    isInCall: false,
    callStatus: null as string | null,
    callDirection: null as "incoming" | "outgoing" | null,
    isOnHold: false,

    // timer chiamata
    callStartTime: null as number | null,
    callDuration: 0,
    callTimerInterval: null as number | null,

    // suoneria
    isRinging: false,
    isOutgoingRinging: false, // ringback in uscita
    answerBeepToken: 0, // trigger one-shot beep risposta

    // stati temporanei di fine chiamata
    temporaryRejected: false,
    temporaryEnded: false,
    temporaryBusy: false,

    lastEndCauseCode: null as number | null,
    lastEndCauseOrigin: null as
      | "local"
      | "remote-reject"
      | "remote-bye"
      | "timeout"
      | "error"
      | null,
    lastEndCauseLabel: "" as string,

    wasCallEstablished: false,

    isCallEstablished: false,
    dtmfHistory: [] as string[],

    // Trasferimento in corso verso un altro interno (GAIA-2776). Finché è
    // attivo la chiamata non è più "solo mia": i comandi che la toccano vanno
    // bloccati, e alla chiusura della sessione questo flag è ciò che distingue
    // "trasferita" da "riagganciata".
    isTransferring: false,
    transferTarget: null as string | null,
    // Consultazione in corso (GAIA-2777): il collega ha risposto, il cliente e'
    // in attesa sul centralino e la decisione e' dell'operatore. E' uno stato
    // distinto da `isTransferring`: li' si aspetta il centralino, qui si aspetta
    // una persona, e i comandi disponibili sono altri.
    isConsulting: false,

    connectionFailure: null as SipConnectionFailure | null,

    // Motivo di pausa dell'operatore nella coda (proiezione read-only dello
    // stato agente del leader). null = non in pausa. Alimenta gli stati
    // "Pausa"/"Back Office" del SipStatusChip. È già RISOLTO dal leader: resta
    // null quando l'interno non è raggiungibile (GAIA-2694).
    pauseReason: null as string | null,

    // Asterisk non raggiunge il proprio interno (device Invalid/Unavailable in
    // coda) mentre la barra si crede registrata: proiezione read-only dal
    // leader, alimenta lo stato "Interno offline" del SipStatusChip (GAIA-2694).
    queueMemberUnreachable: false,
  }),

  getters: {
    callStatusWithTimer: (state) => {
      if (!state.callStatus) return "";

      if (state.isInCall && state.callDuration > 0) {
        const minutes = Math.floor(state.callDuration / 60);
        const seconds = state.callDuration % 60;

        const mm = String(minutes).padStart(2, "0");
        const ss = String(seconds).padStart(2, "0");

        return `${state.callStatus} (${mm}:${ss})`;
      }

      return state.callStatus;
    },
  },

  actions: {
    /**
     * Configura e registra il SIP User Agent con le credenziali fornite.
     */
    async initializeSip(credentials: SipCredentials) {
      this.isLoading = true;
      this.error = null;
      try {
        await setupSIP(credentials);
        this.extension = credentials.username;
        // NB: isRegistered è impostato dal listener su RegistererState.Registered in setupSIP.
        // Non forzarlo qui: se la registrazione fallisce silenziosamente avremmo un falso positivo.
      } catch (err) {
        console.error("Errore durante la configurazione SIP:", err);
        this.error = err instanceof Error ? err.message : "Errore sconosciuto";
        this.isRegistered = false;
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Imposta una chiamata in arrivo (dati base).
     * Chiamato da setupSIP.onInvite.
     */
    setIncomingCall(invitation: Invitation) {
      this.incomingCall = markRaw(invitation);
      this.callDirection = "incoming";
      this.callStatus = "Sta squillando";
      // Nuova chiamata, nessuna scheda ancora risolta. Senza questo reset il
      // pop-up "Apri scheda" resterebbe puntato al chiamante precedente:
      // hangupCall non azzera targetUrl (GAIA-2719).
      this.targetUrl = "";

      const callerId = invitation.remoteIdentity?.uri?.user;
      if (callerId) {
        this.phoneNumber = callerId.replace("++", "+");
        // Numero arrivato dallo squillo: comporlo non è un intento dell'operatore.
        this.phoneNumberFromInbound = true;
      }

      // Riassegnata a ogni squillo, mai lasciata dalla chiamata precedente:
      // una linea residua farebbe rispondere l'operatore col servizio sbagliato.
      this.callerLine = extractCallerLine(invitation.remoteIdentity?.displayName, this.phoneNumber);

      this.startRingtone();
      this.persistRecoverySnapshot();
    },

    /**
     * Collega l'audio remoto della chiamata a un elemento audio HTML per la riproduzione.
     * Chiamato da attachRemoteAudioToCall in sip-call-manager.
     * Idempotente: riusa un unico elemento <audio>, così può essere invocata
     * sia all'arrivo dell'early media (183) sia a chiamata stabilita senza
     * duplicare la riproduzione.
     */
    attachRemoteAudio(stream: MediaStream) {
      if (!remoteAudioElement || !remoteAudioElement.isConnected) {
        remoteAudioElement = document.createElement("audio");
        remoteAudioElement.autoplay = true;
        document.body.appendChild(remoteAudioElement);
      }
      if (remoteAudioElement.srcObject !== stream) {
        remoteAudioElement.srcObject = stream;
      }
    },

    /**
     * Ripristina lo stato relativo alla chiamata in arrivo.
     * Usato quando la chiamata è terminata o rifiutata.
     */
    clearIncomingCall() {
      this.incomingCall = null;
      this.callStatus = "";
      this.callDirection = null;
    },

    /**
     * Imposta il numero come intento esplicito dell'operatore: digitato nella
     * barra, cliccato su una scheda, richiamato dal banner di recovery. Azzera la
     * provenienza inbound, così il tasto di chiamata non chiede conferma su un
     * numero che l'operatore ha scelto lui (GAIA-2664).
     */
    setDialTarget(phoneNumber: string) {
      this.phoneNumber = phoneNumber;
      this.phoneNumberFromInbound = false;
    },

    /**
     * Accetta una chiamata in arrivo.
     * La logica di stato/timer è gestita in monitorSession.
     *
     * Ritorna `no-call` quando non c'è più nulla da rispondere e **rilancia** se
     * l'accept fallisce: entrambi i casi vanno mostrati all'operatore. Prima
     * uscivano da qui in silenzio, e il tasto verde sembrava semplicemente non
     * funzionare (GAIA-2664).
     */
    async acceptCall(): Promise<AnswerOutcome> {
      if (!this.incomingCall) return "no-call";
      await answerCall(this.incomingCall as Invitation);
      return "answered";
    },

    /**
     * Entra nello stato di trasferimento verso `extension`.
     *
     * Fail-fast: senza una chiamata stabilita non c'è niente da trasferire, e
     * un secondo trasferimento sulla stessa chiamata è un doppio comando al
     * centralino sullo stesso canale.
     */
    startTransfer(extension: string) {
      if (!extension) throw new Error("startTransfer: extension is required");
      if (!this.isCallEstablished) {
        throw new Error("startTransfer: nessuna chiamata stabilita da trasferire");
      }
      if (this.isTransferring) {
        throw new Error("startTransfer: trasferimento già in corso");
      }

      this.isTransferring = true;
      this.transferTarget = extension;
    },

    /** Il collega ha risposto: si passa dall'attesa del centralino alla consultazione. */
    startConsultation() {
      this.isTransferring = false;
      this.isConsulting = true;
    },

    /** Esce da trasferimento e consultazione, sia per successo sia per rientro. */
    endTransfer() {
      this.isTransferring = false;
      this.isConsulting = false;
      this.transferTarget = null;
    },

    hangupCall() {
      this.isInCall = false;
      this.callStatus = "Chiamata terminata";
      this.isTransferring = false;
      this.isConsulting = false;
      this.transferTarget = null;

      if (this.callTimerInterval) {
        clearInterval(this.callTimerInterval);
        this.callTimerInterval = null;
      }
      this.callStartTime = null;
      this.callDuration = 0;
      this.clearIncomingCall();
      this.isCallEstablished = false;
      this.dtmfHistory = [];
      clearSnapshot();
    },

    /**
     * Imposta il numero di telefono e impedisce la propagazione dell'evento.
     *
     * Se il popup è già aperto, postPrefillNumber lo raggiunge via
     * BroadcastChannel. Se il popup era CHIUSO, lo apriamo qui (window.open
     * è sicuro perché siamo dentro un click handler — user gesture) e
     * depositiamo il numero nel localStorage via setPendingPrefill: il popup
     * appena avviato lo legge in onMounted.
     *
     * NON viene mai avviata una chiamata automaticamente: questo è solo prefill.
     */
    callPhoneNumber(event: Event, phone: string) {
      event.stopPropagation();
      this.setDialTarget(phone);
      // Prefill live: se il popup è già aperto e in ascolto lo riceve subito.
      postPrefillNumber(phone);
      // Se il popup era chiuso, lo apriamo e lasciamo il numero "in sospeso":
      // la barra appena montata lo riprende (la finestra impiega un attimo a registrarsi).
      if (openSipPopupIfClosed()) {
        setPendingPrefill(phone);
      }
    },

    /**
     * Verifica informazioni sul numero di telefono e imposta un URL target.
     */
    async checkInvitation(pn = null) {
      const response = await getInvitationInfoFromPhoneNumber(pn ?? this.phoneNumber);
      if (!response.data.status || !response.data.id) {
        console.error("Status o ID mancanti nella risposta");
        return;
      }
      this.targetUrl = `/lead/edit/${response.data.id}`;
      this.persistRecoverySnapshot();
    },

    /**
     * Avvia il timer di chiamata.
     * Chiamato da monitorSession su SessionState.Established.
     */
    startCallTimer() {
      if (this.callTimerInterval) {
        clearInterval(this.callTimerInterval);
      }

      this.callStartTime = Date.now();
      this.callDuration = 0;

      this.callTimerInterval = window.setInterval(() => {
        if (!this.callStartTime) return;
        const elapsed = Date.now() - this.callStartTime;
        this.callDuration = Math.floor(elapsed / 1000);
      }, 1000);

      this.persistRecoverySnapshot();
    },

    /**
     * Salva uno snapshot della chiamata corrente in sessionStorage per offrire
     * un banner di recovery dopo un eventuale refresh della tab.
     * No-op se phoneNumber o callDirection non sono ancora settati.
     */
    persistRecoverySnapshot() {
      if (!this.phoneNumber || !this.callDirection) return;
      saveSnapshot({
        phoneNumber: this.phoneNumber,
        callDirection: this.callDirection,
        callStartTime: this.callStartTime ?? Date.now(),
        targetUrl: this.targetUrl || null,
      });
    },

    startRingtone() {
      this.isRinging = true; // inbound
    },

    stopRingtone() {
      this.isRinging = false;
    },

    startOutgoingRingback() {
      this.isOutgoingRinging = true;
    },

    stopOutgoingRingback() {
      this.isOutgoingRinging = false;
    },

    triggerAnswerBeep() {
      // incremento: mi serve solo per innescare il watcher, di base non serve ad un cazzo di niente
      // non mi vengono idee più furbe
      this.answerBeepToken++;
    },

    setTemporaryRejected() {
      this.temporaryRejected = true;
      setTimeout(() => {
        this.temporaryRejected = false;
      }, 3000);
    },

    setTemporaryEnded() {
      this.temporaryEnded = true;
      setTimeout(() => {
        this.temporaryEnded = false;
      }, 3000);
    },

    setTemporaryBusy() {
      this.temporaryBusy = true;
      setTimeout(() => {
        this.temporaryBusy = false;
      }, 3000);
    },

    clearEndCause() {
      this.lastEndCauseCode = null;
      this.lastEndCauseOrigin = null;
      this.lastEndCauseLabel = "";
    },

    setEndCause(
      code: number | null,
      origin: "local" | "remote-reject" | "remote-bye" | "timeout" | "error"
    ) {
      this.lastEndCauseCode = code ?? null;
      this.lastEndCauseOrigin = origin;
      this.lastEndCauseLabel = this.mapEndCause(code, origin);

      this.autoClearEndCause();
    },

    setConnectionFailure(failure: SipConnectionFailure) {
      this.connectionFailure = failure;
      this.isRegistered = false;
    },

    clearConnectionFailure() {
      this.connectionFailure = null;
    },

    autoClearEndCause(delay = 3000) {
      setTimeout(() => {
        this.clearEndCause();
      }, delay);
    },

    mapEndCause(code: number | null, origin: string) {
      switch (origin) {
        case "local":
          return "Chiamata terminata dall'operatore";
        case "remote-reject":
          if (code === 486) return "Linea occupata (486 Busy Here)";
          if (code === 603) return "Chiamata rifiutata (603 Declined)";
          return `Chiamata rifiutata (codice ${code ?? "sconosciuto"})`;
        case "remote-bye":
          // Differenzia in base a se la chiamata era stata stabilita o meno
          return this.wasCallEstablished
            ? "Chiamata terminata dal destinatario"
            : "Chiamata rifiutata";
        case "timeout":
          return "Nessuna risposta / timeout";
        case "error":
          return "Errore di chiamata / rete";
        default:
          return "Chiamata terminata";
      }
    },

    pushDtmfTone(tone: string) {
      this.dtmfHistory.push(tone);
    },

    clearDtmfHistory() {
      this.dtmfHistory = [];
    },

    /**
     * Applica uno snapshot di stato chiamata pubblicato dal leader SIP
     * (tipicamente la window popup dedicata) tramite BroadcastChannel.
     *
     * Usato dalla main app in modalità follower: lo store locale non ha
     * la sessione SIP attiva, quindi viene rispecchiato lo stato del leader
     * per alimentare il chip in Header. NON tocca incomingCall né timer:
     * quelli sono entità live del leader e non possono essere replicate
     * cross-window in modo sensato.
     */
    applyRemoteSnapshot(payload: SipCallStateSnapshot) {
      this.isRegistered = payload.isRegistered;
      // Rispecchia l'interno del leader così il centralino (follower) sa quale
      // card/chip è la propria (GAIA-2589 Scen. 2).
      this.extension = payload.extension;
      this.isInCall = payload.isInCall;
      this.isCallEstablished = payload.isCallEstablished;
      this.callStatus = payload.callStatus;
      this.callDuration = payload.callDuration;
      this.phoneNumber = payload.phoneNumber;
      this.callDirection = payload.callDirection;
      this.isRinging = payload.hasIncomingRing;
      this.connectionFailure = payload.connectionFailureMessage
        ? { type: "transport", message: payload.connectionFailureMessage }
        : null;
      this.pauseReason = payload.pauseReason ?? null;
      this.queueMemberUnreachable = payload.queueMemberUnreachable ?? false;
    },
  },
});
